import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useLightbox } from "@/components/Lightbox";
import { PlanningDialog } from "@/components/PlanningDialog";
import { InterventionDialog } from "@/components/care/InterventionDialog";
import { careFileUrl } from "@/lib/care";
import { AnStatusBadge, AN_STATUS_META, W_STATE_META, schedLabel } from "@/lib/sav";
import { fmtDate, fmtDateTime, todayIso } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const COVERAGE = { a_examiner: "À examiner", confirmee: "Prise en charge confirmée", non: "Non prise en charge" };
const STATES = { bon_etat: "Bon état apparent", a_controler: "À contrôler", intervention_a_prevoir: "Intervention à prévoir", hors_service: "Hors service", retire: "Retiré du service" };
const Section = ({ title, children, testId, action }) => (
  <Card className="p-4" data-testid={testId}>
    <div className="mb-3 flex items-center justify-between gap-2"><h2 className="font-display text-base font-semibold text-[#0D2E24]">{title}</h2>{action}</div>
    {children}
  </Card>
);
const Photos = ({ files, lb }) => files.length === 0 ? null : (
  <div className="mt-2 flex flex-wrap gap-1.5">{files.map((p, i) => (
    p.content_type?.startsWith("image/") ? <img key={p.id} src={careFileUrl(p.id)} alt="" onClick={() => lb.open(files.filter((x) => x.content_type?.startsWith("image/")).map((x) => ({ src: careFileUrl(x.id) })), i)} className="h-16 w-16 cursor-zoom-in rounded object-cover" data-testid={`an-photo-${p.id}`} />
      : <a key={p.id} href={careFileUrl(p.id)} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">{p.original_filename}</a>))}</div>
);

function StatusDialog({ open, onOpenChange, a, onDone }) {
  const [f, setF] = useState({ status: "", reason: "", action: "", actual_date: todayIso() });
  useEffect(() => { if (open) setF({ status: a.status, reason: "", action: "", actual_date: todayIso() }); }, [open, a.status]);
  const reopening = ["resolu", "clos_sans"].includes(a.status) && !["resolu", "clos_sans"].includes(f.status);
  const save = async () => {
    try { await api.post(`/care/anomalies/${a.id}/status`, f); toast.success("Statut mis à jour"); onOpenChange(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="an-status-dialog">
        <DialogHeader><DialogTitle>Changer le statut</DialogTitle>
          <DialogDescription>La fin d'une intervention ne résout pas automatiquement le signalement : la résolution doit être confirmée ici.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="an-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(AN_STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          {f.status === "resolu" && a.status !== "resolu" && <>
            <div className="space-y-1.5"><Label>Action réalisée *</Label><Textarea value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} data-testid="an-status-action" /></div>
            <div className="space-y-1.5"><Label>Date réelle *</Label><Input type="date" value={f.actual_date} onChange={(e) => setF({ ...f, actual_date: e.target.value })} data-testid="an-status-date" /></div>
          </>}
          {(f.status === "clos_sans" && a.status !== "clos_sans" || reopening) && (
            <div className="space-y-1.5"><Label>{reopening ? "Raison de la réouverture *" : "Motif de clôture *"}</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="an-status-reason" /></div>)}
        </div>
        <DialogFooter><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="an-status-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Pilot({ a, users, canManage, reload }) {
  const [cov, setCov] = useState({ coverage_decision: a.coverage_decision || "a_examiner", coverage_message: a.coverage_message || "" });
  const patch = async (body, msg) => { try { await api.patch(`/care/anomalies/${a.id}`, body); toast.success(msg); reload(); } catch (e) { toast.error(apiError(e)); } };
  const day = (a.created_at || "").slice(0, 10);
  return (
    <Section title="Traitement" testId="an-pilot">
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Responsable du traitement</Label>
          <Select value={a.assignee_id || "none"} onValueChange={(v) => patch({ assignee_id: v === "none" ? null : v }, "Responsable mis à jour")} disabled={!canManage}>
            <SelectTrigger data-testid="an-assignee"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Non affecté</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs font-semibold text-slate-500">Garanties à la date du signalement ({fmtDate(day)})</p>
          {(a.warranties || []).length === 0 ? <p className="text-sm text-slate-400">Garantie non renseignée.</p>
            : <ul className="mt-1 space-y-1">{a.warranties.map((w) => (
                <li key={w.id} className="flex flex-wrap items-center gap-1.5 text-sm">{w.kind_label}{w.provider ? ` — ${w.provider}` : ""} · {w.end_date ? `fin ${fmtDate(w.end_date)}` : "fin non renseignée"}
                  <span className={`rounded-full border px-1.5 text-[10px] ${w.active_at_report ? W_STATE_META.valide : W_STATE_META.depassee}`}>{w.active_at_report ? "en vigueur au signalement" : "hors période"}</span></li>))}</ul>}
          <p className="mt-1 text-[11px] text-slate-400">Une garantie en vigueur ne vaut pas prise en charge : la décision est à renseigner ci-dessous.</p>
        </div>
        <div className="space-y-1.5"><Label>Décision de prise en charge</Label>
          <Select value={cov.coverage_decision} onValueChange={(v) => setCov({ ...cov, coverage_decision: v })} disabled={!canManage}>
            <SelectTrigger data-testid="an-coverage"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(COVERAGE).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={cov.coverage_message} onChange={(e) => setCov({ ...cov, coverage_message: e.target.value })} placeholder="Explication destinée au client" disabled={!canManage} data-testid="an-coverage-msg" />
          {canManage && <Button size="sm" variant="outline" onClick={() => patch(cov, "Décision enregistrée")} data-testid="an-coverage-save">Enregistrer la décision</Button>}
          <p className="text-[11px] text-slate-400">Visible par le client uniquement si « confirmée » ou « non prise en charge ».</p>
        </div>
      </div>
    </Section>
  );
}

function InterventionBlock({ a, canManage, reload }) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(false);
  const [replan, setReplan] = useState(false);
  const [attach, setAttach] = useState("");
  const ws = a.worksite;
  const publish = async () => {
    try { const { data } = await api.post(`/care/interventions/${ws.id}/publish-schedule`); toast.success(data.emailed ? `Date publiée — e-mail envoyé (${data.emailed})` : "Date publiée dans l'espace client (aucun e-mail envoyé : pas d'adresse ou envoi indisponible)"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doAttach = async () => {
    try { await api.post(`/care/anomalies/${a.id}/attach`, { worksite_id: attach }); toast.success("Signalement rattaché à l'intervention"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Section title="Intervention" testId="an-intervention">
      {ws ? (
        <div className="space-y-2 text-sm">
          <button onClick={() => navigate(`/track/${ws.id}`)} className="font-medium text-emerald-700 hover:underline" data-testid="an-ws-link">{ws.reference} · {ws.title}</button>
          <p className="text-slate-600">Statut chantier : {ws.status_label}</p>
          <p data-testid="an-ws-internal">Date interne : {schedLabel(ws.schedule_confirmed ? ws : null, fmtDate) || "non planifiée"}</p>
          <p data-testid="an-ws-client">Date publiée au client : {schedLabel(ws.client_schedule, fmtDate) || "aucune"}</p>
          {ws.schedule_unpublished && <p className="rounded bg-amber-50 p-2 text-xs text-amber-800" data-testid="an-ws-unpublished">La date interne diffère de celle publiée au client.</p>}
          {canManage && <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => setReplan(true)} data-testid="an-replan"><Icons.CalendarClock className="mr-1 h-4 w-4" />Replanifier</Button>
            {ws.kind === "sav" && <Button size="sm" onClick={publish} disabled={!ws.schedule_unpublished} className="bg-emerald-600 hover:bg-emerald-700" data-testid="an-publish-date"><Icons.Send className="mr-1 h-4 w-4" />Publier la date au client</Button>}
          </div>}
          <PlanningDialog open={replan} onOpenChange={setReplan} ws={{ ...ws, category: "planifie" }} onSaved={reload} />
        </div>
      ) : canManage ? (
        <div className="space-y-3">
          <Button onClick={() => setPlan(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="an-plan"><Icons.CalendarPlus className="mr-1.5 h-4 w-4" />Planifier une intervention</Button>
          {(a.candidate_worksites || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={attach} onValueChange={setAttach}>
                <SelectTrigger className="w-64" data-testid="an-attach-select"><SelectValue placeholder="Intervention existante sur ce site" /></SelectTrigger>
                <SelectContent>{a.candidate_worksites.map((w) => <SelectItem key={w.id} value={w.id}>{w.reference} · {w.title}{w.planned_start ? ` (${fmtDate(w.planned_start)})` : ""}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" disabled={!attach} onClick={doAttach} data-testid="an-attach">Rattacher</Button>
            </div>)}
          <InterventionDialog open={plan} onOpenChange={setPlan} siteId={a.site_id} equipment={a.site_equipment} preselect={[a.equipment_id]} anomalyIds={[a.id]} onDone={reload} />
        </div>
      ) : <p className="text-sm text-slate-400">Aucune intervention.</p>}
    </Section>
  );
}

function Thread({ a, lb, reload }) {
  const [text, setText] = useState("");
  const send = async () => {
    try { const { data } = await api.post(`/care/anomalies/${a.id}/reply`, { text }); setText(""); toast.success(data.emailed ? `Réponse publiée — e-mail envoyé (${data.emailed})` : "Réponse publiée dans l'espace client (aucun e-mail envoyé)"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const files = a.files || [];
  return (
    <Section title="Échanges avec le client" testId="an-thread">
      <ul className="space-y-2">{(a.messages || []).map((m) => (
        <li key={m.id} className={`rounded-lg p-2.5 text-sm ${m.author === "team" ? "ml-8 bg-emerald-50" : "mr-8 bg-slate-50"}`} data-testid={`an-msg-${m.id}`}>
          <p className="text-xs text-slate-400">{m.author === "team" ? `Équipe · ${m.by_name}` : `Client · ${m.by_name}`} · {fmtDateTime(m.at)}</p>
          {m.text && <p className="whitespace-pre-line text-slate-700">{m.text}</p>}
          <Photos files={files.filter((f) => f.message_id === m.id)} lb={lb} />
        </li>))}
        {(a.messages || []).length === 0 && <p className="text-sm text-slate-400">Aucun échange pour le moment.</p>}</ul>
      <div className="mt-3 flex gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Répondre au client (visible dans son espace)" data-testid="an-reply-input" />
        <Button onClick={send} disabled={!text.trim()} className="self-end bg-emerald-600 hover:bg-emerald-700" data-testid="an-reply-send"><Icons.Send className="h-4 w-4" /></Button>
      </div>
    </Section>
  );
}

function Notes({ a, reload }) {
  const [text, setText] = useState("");
  const add = async () => { try { await api.post(`/care/anomalies/${a.id}/note`, { text }); setText(""); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Section title="Notes internes" testId="an-notes">
      <p className="mb-2 text-xs text-slate-400"><Icons.Lock className="mr-1 inline h-3 w-3" />Jamais visibles par le client.</p>
      <ul className="space-y-1.5">{(a.internal_notes || []).map((n) => <li key={n.id} className="rounded bg-amber-50/60 p-2 text-sm"><span className="text-xs text-slate-400">{n.by_name} · {fmtDateTime(n.at)}</span><p className="whitespace-pre-line">{n.text}</p></li>)}</ul>
      <div className="mt-2 flex gap-2"><Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ajouter une note interne" data-testid="an-note-input" />
        <Button variant="outline" onClick={add} disabled={!text.trim()} data-testid="an-note-add">Ajouter</Button></div>
    </Section>
  );
}

function Reports({ a, lb, reload }) {
  const [text, setText] = useState("");
  const [vis, setVis] = useState(false);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const add = async () => {
    const fd = new FormData(); fd.append("text", text); fd.append("client_visible", vis ? "true" : "false");
    Array.from(ref.current?.files || []).forEach((f) => fd.append("files", f));
    setBusy(true);
    try { await api.post(`/care/anomalies/${a.id}/report`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setText(""); if (ref.current) ref.current.value = ""; setCount(0); toast.success("Compte rendu ajouté"); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Section title="Comptes rendus d'intervention" testId="an-reports">
      <ul className="space-y-2">{(a.reports || []).map((r) => (
        <li key={r.id} className="rounded-lg border border-slate-200 p-2.5 text-sm"><p className="text-xs text-slate-400">{r.by_name} · {fmtDateTime(r.at)} · {r.client_visible ? "Photos visibles par le client" : "Interne"}</p>
          {r.text && <p className="whitespace-pre-line">{r.text}</p>}<Photos files={(a.files || []).filter((f) => r.file_ids?.includes(f.id))} lb={lb} /></li>))}</ul>
      <div className="mt-2 space-y-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Compte rendu (interne)" data-testid="an-report-text" />
        <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => setCount(e.target.files?.length || 0)} data-testid="an-report-files" />
        <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} data-testid="an-report-photo-btn"><Icons.Camera className="mr-1 h-4 w-4" />{count ? `${count} photo(s) sélectionnée(s)` : "Ajouter des photos"}</Button>
        <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={vis} onCheckedChange={(v) => setVis(!!v)} data-testid="an-report-visible" />Rendre les photos visibles par le client</label>
        <Button size="sm" variant="outline" onClick={add} disabled={busy} data-testid="an-report-add">{busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Ajouter le compte rendu"}</Button>
      </div>
    </Section>
  );
}

function Closure({ a, canManage, reload }) {
  const [st, setSt] = useState({ result_status: "", observations: "", publish: true });
  const [res, setRes] = useState(a.client_result?.text || a.resolution?.action || a.close_reason || "");
  const saveState = async () => { try { await api.post(`/care/anomalies/${a.id}/equipment-state`, st); toast.success("État de l'équipement mis à jour"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const publish = async () => { try { const { data } = await api.post(`/care/anomalies/${a.id}/publish-result`, { text: res }); toast.success(data.emailed ? `Résultat publié — e-mail envoyé (${data.emailed})` : "Résultat publié (aucun e-mail envoyé)"); reload(); } catch (e) { toast.error(apiError(e)); } };
  if (!canManage) return null;
  return (
    <Section title="Vérification et résultat" testId="an-closure">
      <p className="text-sm text-slate-600">État actuel de l'équipement : <strong>{a.equipment?.status_label}</strong></p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <Select value={st.result_status} onValueChange={(v) => setSt({ ...st, result_status: v })}>
          <SelectTrigger data-testid="an-eqstate-select"><SelectValue placeholder="Nouvel état après vérification" /></SelectTrigger>
          <SelectContent>{Object.entries(STATES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={st.observations} onChange={(e) => setSt({ ...st, observations: e.target.value })} placeholder="Observations" data-testid="an-eqstate-obs" />
      </div>
      <label className="mt-2 flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={st.publish} onCheckedChange={(v) => setSt({ ...st, publish: !!v })} data-testid="an-eqstate-publish" />Publier ce contrôle dans l'espace client</label>
      <Button size="sm" variant="outline" className="mt-2" disabled={!st.result_status} onClick={saveState} data-testid="an-eqstate-save">Enregistrer l'état</Button>
      {["resolu", "clos_sans"].includes(a.status) && (
        <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">
          <Label>Résultat destiné au client</Label>
          <Textarea value={res} onChange={(e) => setRes(e.target.value)} data-testid="an-result-text" />
          <Button size="sm" onClick={publish} className="bg-emerald-600 hover:bg-emerald-700" data-testid="an-result-publish">{a.client_result ? "Republier le résultat" : "Publier le résultat"}</Button>
          {a.client_result && <p className="text-xs text-slate-400">Publié le {fmtDateTime(a.client_result.published_at)}</p>}
        </div>)}
    </Section>
  );
}

export default function CareAnomaly() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("care.manage");
  const [a, setA] = useState(null);
  const [users, setUsers] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const lb = useLightbox();
  const load = useCallback(() => { api.get(`/care/anomalies/${id}`).then((r) => setA(r.data)).catch(() => setA(false)); }, [id]);
  useEffect(() => { load(); api.get("/users/assignable").then((r) => setUsers(r.data || [])).catch(() => {}); }, [load]);
  useEffect(() => {
    if (a?.team_unread) api.post(`/care/anomalies/${id}/mark-read`).then(() => window.dispatchEvent(new Event("cs-req-changed"))).catch(() => {});
  }, [a?.team_unread, id]);
  if (a === false) return <Card className="p-6 text-center text-sm text-rose-600" data-testid="an-error">Signalement introuvable.</Card>;
  if (!a) return <div className="py-16 text-center text-slate-400">Chargement…</div>;
  const rootFiles = (a.files || []).filter((f) => f.uploaded_by === "client" && !f.message_id);
  return (
    <div className="space-y-4" data-testid="an-detail-page">
      <button onClick={() => navigate("/care?tab=anomalies")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="an-back"><Icons.ChevronLeft className="h-4 w-4" />Anomalies équipements</button>
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2"><span className="font-mono-data text-xs text-slate-400">{a.reference}</span><AnStatusBadge status={a.status} testId="an-status" /></div>
            <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">{a.type_label}</h1>
            <button onClick={() => navigate(`/care/${a.equipment_id}`)} className="text-sm font-medium text-emerald-700 hover:underline" data-testid="an-eq-link">{a.equipment_reference} · {a.equipment_name}</button>
            <p className="text-sm text-slate-500">{a.client_name} — {a.site_name}</p>
            <p className="mt-1 text-xs text-slate-400">Signalé par {a.author_name} le {fmtDateTime(a.created_at)}{a.observed_date ? ` · constaté le ${fmtDate(a.observed_date)}` : ""}</p>
          </div>
          {canManage && <Button variant="outline" onClick={() => setStatusOpen(true)} data-testid="an-status-btn"><Icons.RefreshCw className="mr-1.5 h-4 w-4" />Changer le statut</Button>}
        </div>
        <p className="mt-3 whitespace-pre-line rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid="an-description">{a.description}</p>
        {(a.location_detail || a.group_item) && <p className="mt-2 text-sm text-slate-600">{a.group_item ? `Élément concerné : ${a.group_item}. ` : ""}{a.location_detail ? `Emplacement : ${a.location_detail}` : ""}</p>}
        {a.equipment?.mode === "ensemble" && <p className="mt-1 text-xs text-slate-500">Ensemble de {a.equipment.quantity} {a.equipment.unit}</p>}
        <Photos files={rootFiles} lb={lb} />
        {a.resolution && a.status === "resolu" && <p className="mt-2 text-sm text-emerald-700">Résolu le {fmtDate(a.resolution.date)} : {a.resolution.action}</p>}
        {a.close_reason && a.status === "clos_sans" && <p className="mt-2 text-sm text-slate-600">Motif de clôture : {a.close_reason}</p>}
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4"><Thread a={a} lb={lb} reload={load} /><InterventionBlock a={a} canManage={canManage} reload={load} /><Closure a={a} canManage={canManage} reload={load} /></div>
        <div className="space-y-4"><Pilot a={a} users={users} canManage={canManage} reload={load} /><Notes a={a} reload={load} /><Reports a={a} lb={lb} reload={load} />
          <Section title="Historique" testId="an-history"><ul className="space-y-1 text-xs text-slate-500">{(a.history || []).slice().reverse().map((h, i) => <li key={i}>{fmtDateTime(h.at)} · {h.by_name} · {h.detail || h.action}</li>)}</ul></Section>
        </div>
      </div>
      {lb.node}
      <StatusDialog open={statusOpen} onOpenChange={setStatusOpen} a={a} onDone={load} />
    </div>
  );
}
